import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  styled,
  TextField,
  Typography,
  Tabs,
  Tab
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useSearchAndSort } from "../../hooks/useSearchAndSort";
import IncompleteLoanModal from "../../components/modals/IncompleteLoanModal";
import PreviewApplication from "../apply-loan/PreviewApplication";
import CommonTable from "../../components/common/CommonTable";
import ApplyLoanNowModal from "../../components/leads/ApplyLoanNowModal";
import { loansColumns } from "../../constants/tableColumns";
import { setFormData, setFiles, setActiveStep, setResumed } from "../../Store/userSlice";
import {
  fetchCMADetails,
  getApplicationDetails,
  getResumeProposals,
  getAllProposalsCustomized,
} from "../../services/loanService";
import { toast } from "react-toastify";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: "24px",
});

const PageHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontWeight: 600,
  fontSize: "24px",
});

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = "#4CAF50";
  let backgroundColor = "#E8F5E9";

  if (status === "Pending" || status === "pending" || status === "incomplete") {
    color = "#FFA000";
    backgroundColor = "#FFF8E1";
  } else if (status === "Rejected" || status === "rejected") {
    color = "#F44336";
    backgroundColor = "#FFEBEE";
  } else if (status === "Processing" || status === "processing") {
    color = "#2196F3";
    backgroundColor = "#E3F2FD";
  } else if (status === "Submitted" || status === "submitted") {
    color = "#4CAF50";
    backgroundColor = "#E8F5E9";
  }

  return {
    color: color,
    backgroundColor: backgroundColor,
    fontWeight: 600,
    borderRadius: "16px",
  };
});

const formatCurrency = (amount) => {
  if (!amount || amount === 0 || amount === "0") return "₹0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const FIELD_MAP = new Map([
  ['applicationNumber', 'applicationNumber'],
  ['borrowerName', 'name'],
  ['mobileNumber', 'mobileNo'],
  ['amount', 'fundRequirement'],
  ['submittedDate', 'submittedDate'],
  ['status', 'status'],
  ['offers', 'offers'],
]);

const getSortByField = (columnId) => FIELD_MAP.get(columnId) || columnId;

const Loans = () => {
  const location = useLocation();
  const [loanApplications, setLoanApplications] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0); // New state for total records;  
  const [loading, setLoading] = useState(true);
  const initialTab = location.state?.activeTab === 1 ? 1 : (new URLSearchParams(location.search).get("tab") === "saved" ? 1 : 0);
  const [activeTab, setActiveTab] = useState(initialTab); // 0: All Proposals, 1: Saved Proposals
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, `loans-${activeTab}`);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanDetailsModalOpen, setLoanDetailsModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [showApplyLoanModal, setShowApplyLoanModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const userPermission = useSelector((state) => state.user.userPermissions);
  const [permission, setPermissions] = useState({});
  useEffect(() => {
    console.log("User Permissions:", userPermission);
    const moduleList = userPermission?.map((e) => e?.module);
    if (moduleList && moduleList.length > 0) {
      const hasLeadsPermission = moduleList.includes("loans");
      if (!hasLeadsPermission) {
        //   showToast("You do not have permission to access Leads", "error");
        navigate("/dashboard");
      } else {
        console.log(
          "User Permissions for Leads:",
          userPermission.find(
            (e) => e.module === "loans"
          )
        );
        setPermissions(
          JSON.parse(
            userPermission.find((e) => e.module === "loans")?.permission
          ) || null
        );
      }
    }
  }, [userPermission, navigate]);

  const parseFundRequirement = useCallback((value) => {
    if (!value) return "N/A";

    if (value === "1_5L") return "₹1-5 Lakhs";
    if (value === "5_10L") return "₹5-10 Lakhs";
    if (value === "10-50") return "₹10-50 Lakhs";
    if (value === "50-100") return "₹50-100 Lakhs";

    return value;
  }, []);

  const getFormattedDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "N/A";
    }
  }, []);

  const transformLoanApplicationData = useCallback((application) => ({
    ...application,
    id: application.applicationId || application.uuid || application.id,
    proposalId: application?.proposalId ?? "N/A",
    applicationId: application?.applicationId || "N/A",
    applicationNumber: application?.applicationNumber || "N/A",
    borrowerName: application?.borrowerName || "N/A",
    mobileNumber: application?.borrowerMobileNumber || "N/A",
    amount: parseFundRequirement(application.fundRequirement),
    submittedDate: getFormattedDate(application.commitDatetime || application.createDatetime),
    status: application.status || "Submitted",
    proposal: application.proposal,
    proposalDetails: {
      id: application.proposalId,
      cma: application.cma,
      onePager: application.onePager,
      proposalBrief: application.proposalBrief,
      status: application.proposalStatus,
    },
    offerForProposal: Array.isArray(application.offerForProposal)
      ? application.offerForProposal
      : [],
  }), [parseFundRequirement, getFormattedDate]);

  const transformSavedApplication = useCallback((item) => ({
    ...item,
    id: item.uuid || item.applicationId || item.id,
    proposalId: item.applicationNumber || `APP-${item.applicationId || ""}`,
    applicationId: item.applicationId || "N/A",
    applicationNumber: item.applicationNumber || "N/A",
    borrowerName: item?.userId?.name || item?.nameAsPerGST || "N/A",
    mobileNumber: item?.userId?.mobileNumber || "N/A",
    amount: parseFundRequirement(item?.fundRequirement),
    submittedDate: getFormattedDate(item?.createDatetime || item?.createdAt),
    status: "incomplete",
    proposalDetails: null,
  }), [parseFundRequirement, getFormattedDate]);

  const fetchLoans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const isAllProposals = activeTab === 0;
      const sortByField = searchAndSortParams.sortBy ? getSortByField(searchAndSortParams.sortBy) : searchAndSortParams.sortBy;
        const response = isAllProposals 
          ? await getAllProposalsCustomized(page, rowsPerPage, sortByField, searchAndSortParams.order, searchAndSortParams.searchQuery)
          : await getResumeProposals(page, rowsPerPage, sortByField, searchAndSortParams.order, searchAndSortParams.searchQuery);

      if (isAllProposals) {
        const list = response?.result?.data || response?.result || response?.data?.content || response?.content || [];
        const transformedData = Array.isArray(list) ? list.map(transformLoanApplicationData) : [];
        setLoanApplications(transformedData);
        const total = response?.result?.rowCount || response?.rowCount || response?.data?.totalElements || response?.totalElements || transformedData.length;
        setTotalRecords(total || 0);
      } else {
        const list = response?.result?.data || response?.result || response?.data || response?.content || [];
        const transformed = Array.isArray(list) ? list.map(transformSavedApplication) : [];
        setLoanApplications(transformed);
        const total = response?.rowCount || response?.totalElements || transformed.length;
        setTotalRecords(total);
      }
    } catch (error) {
      toast.error("Error fetching loan applications:", error);
      setError(error.message || "Failed to load loan applications");
      setSnackbar({
        open: true,
        message: error.message || "Failed to load loan applications",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, rowsPerPage, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order, transformLoanApplicationData, transformSavedApplication]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Reference error state to avoid unused warning while preserving behavior
  useEffect(() => {
    if (error) {
      console.error(error);
      }
  }, [error]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleApplyLoan = useCallback(() => {
    setShowApplyLoanModal(true);
  }, []);

  const handleViewApplication = useCallback((application) => {
    if (!application?.applicationId) {
      console.error(
        "Missing applicationId for viewing application:",
        application
      );
      setSnackbar({
        open: true,
        message: "Cannot view this application: Missing application ID",
        severity: "error",
      });
      return;
    }
    getApplicationDetails(application.applicationId)
      .then((response) => {
        if (response && (response.statusCode === 200 || response.responseCode === 200)) {
          const apiData = response?.data?.data || response?.result?.data || response?.result || {};
          const documents = Array.isArray(apiData?.documents) ? apiData.documents : [];
          const typeToKeyMap = {
            BankStatement: 'bankStatement',
            SLLoan: 'slLoan',
            DebitProfile: 'debitProfile',
            CoLetteral: 'coLetteral',
            OrderCopy: 'orderCopy',
            ContractCopy: 'contractCopy',
            Certification: 'certification',
            CreditRating: 'creditRating',
            'Aadhar Director': 'AadharCard',
            'PAN Director': 'panCard',
            BalanceSheet: 'auditedBalanceFYPrev1',
            MOA: 'moa',
            AOA: 'aoa',
            'Other Relevant Docs': 'other',
          };
          const docMap = documents.reduce((acc, doc) => {
            const key = typeToKeyMap[doc?.documentType];
            if (!key) return acc;
            if (acc[key]) {
              const current = Array.isArray(acc[key]) ? acc[key] : [acc[key]];
              acc[key] = [...current, doc];
            } else {
              acc[key] = [doc];
            }
            return acc;
          }, {});
          const userInfo = {
            Name: apiData?.userId?.name || application?.userId?.name || "",
            Email: apiData?.userId?.email || application?.userId?.email || "",
            MobileNumber: apiData?.userId?.mobileNumber || application?.userId?.mobileNumber || "",
          };
          const combined = { ...apiData, loanDoc: { ...(apiData?.loanDoc || {}), ...docMap }, userInfo };
          setPreviewData(combined);
          setPreviewOpen(true);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load application details",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        toast.error("Error loading application details:", error);
        setSnackbar({
          open: true,
          message: "Failed to load application details",
          severity: "error",
        });
      });
  }, []);

  const handleCMAGeneration = useCallback((application) => {
    if (!application.applicationId) {
      console.error("Missing applicationId for CMA generation:", application);
      setSnackbar({
        open: true,
        message: "Cannot generate CMA: Missing application ID",
        severity: "error",
      });
      return;
    }

    if (application.proposalDetails?.cma) {
      window.open(application.proposalDetails.cma, "_blank", "noopener,noreferrer");

      setSnackbar({ 
        open: true, 
        message: "CMA Report download started", 
        severity: "success" 
      });
    } else {
      fetchCMADetails(application.applicationId)
        .then((response) => {
          console.log("CMA Details:", response);
          setSnackbar({ 
            open: true, 
            message: "CMA report requested", 
            severity: "success" 
          });
        })
        .catch((error) => {
          console.error("Error fetching CMA details:", error);
          setSnackbar({
            open: true,
            message: "Failed to fetch CMA details",
            severity: "error",
          });
        });
    }
  }, []);

  const handleOnePagerDownload = useCallback((application) => {
    if (!application.applicationId) {
      setSnackbar({
        open: true,
        message: "Cannot download One Pager: Missing application ID",
        severity: "error",
      });
      return;
    }

    if (application.proposalDetails?.onePager) {
      window.open(application.proposalDetails.onePager, "_blank", "noopener,noreferrer");

      setSnackbar({ 
        open: true, 
        message: "One Pager download started", 
        severity: "success" 
      });
    }
  }, []);

  const handleCloseLoanDetailsModal = useCallback(() => {
    setLoanDetailsModalOpen(false);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewData(null);
  }, []);

  useMemo(() => (setSelectedLoan ? null : null), [setSelectedLoan]);

  const filteredLoanApplications = useMemo(() => {
    return loanApplications;
  }, [loanApplications]);

  const handleViewProposal = useCallback((application) => {
    if (!application.applicationId || !application.proposalDetails) {
      setSnackbar({
        open: true,
        message: "Cannot view proposal: Missing proposal details",
        severity: "error",
      });
      return;
    }

    navigate(
      `/proposals/details/${application.proposalDetails.id || application.applicationId        
      }`,
      {
        state: {
          proposalData: application.proposalDetails,
          applicationData: application,
        },
      }
    );
  }, [navigate]);

  const handleUpdateProposal = useCallback((application) => {
    if (!application.applicationId || !application.proposalDetails) {
      setSnackbar({
        open: true,
        message: "Cannot update proposal: Missing proposal details",
        severity: "error",
      });
      return;
    }

    navigate(
      `/create-proposal/${application.applicationId}`,
      {
        state: {
          proposalData: application.proposalDetails,
          isUpdate: true,
          lenders: application.lenders || []
        },
      }
    );
  }, [navigate]);

  const getEntityTypeLabel = useCallback((entityType, borrowerType) => {
    if (entityType === "individual") {
      return "Individual";
    } else if (entityType === "entity") {
      if (borrowerType) {
        const formattedBusinessType =
          borrowerType.charAt(0).toUpperCase() + borrowerType.slice(1);
        return `Entity - ${formattedBusinessType}`;
      }
      return "Entity";
    }
    return "N/A";
  }, []);

  // Keep currently unused imports/helpers referenced to satisfy linter without changing behavior
  useMemo(() => {
    return (FilterListIcon && Card && CardContent && Paper && formatCurrency && getEntityTypeLabel)
      ? null
      : null;
  }, [getEntityTypeLabel]);

  const handleCreateProposal = useCallback((application) => {
    if (!application.applicationId) {
      setSnackbar({
        open: true,
        message: "Cannot create proposal: Missing application ID",
        severity: "error",
      });
      return;
    }
    navigate(`/create-proposal/${application.applicationId}`);
  }, [navigate]);

  const handleResumeApplication = useCallback((row) => {
    if (!row?.applicationId) {
      setSnackbar({ open: true, message: "Missing application ID", severity: "error" });
      return;
    }

    getApplicationDetails(row.applicationId)
      .then((response) => {
        if (!(response?.statusCode === 200 || response?.responseCode === 200)) {
          throw new Error("Failed to load application details");
        }

        const apiData = response?.data?.data || response?.result?.data || response?.result || {};
        const documents = Array.isArray(apiData?.documents) ? apiData.documents : [];

        const normalizeBorrowerType = (v) => {
          const val = (v || '').toString().toLowerCase().trim();
          return ['entity', 'business', 'company', 'partnership', 'proprietorship', 'firm'].includes(val) ? 'entity' : 'individual';
        };
        const normalizeEntityType = (v) => {
          const val = (v || '').toString().toLowerCase().trim();
          if (['company', 'private company', 'pvt ltd', 'pvt. ltd.', 'private limited', 'pvt'].includes(val)) return 'company';
          if (['partnership', 'partnership_firm', 'partnership firm'].includes(val)) return 'partnership_firm';
          if (['proprietorship', 'propreitorship_firm', 'proprietorship_firm', 'sole proprietorship', 'sole-proprietorship'].includes(val)) return 'propreitorship_firm';
          return val;
        };
        const normalizedBorrowerType = normalizeBorrowerType(apiData?.borrowerType);
        const normalizedEntityType = normalizedBorrowerType === 'entity' ? normalizeEntityType(apiData?.entityType) : '';

        const typeToKeyMap = {
          BankStatement: 'bankStatement', SLLoan: 'slLoan', DebitProfile: 'debitProfile',
          CoLetteral: 'coLetteral', OrderCopy: 'orderCopy', ContractCopy: 'contractCopy',
          Certification: 'certification', CreditRating: 'creditRating', CompanyBrief: 'companyBrief',
          MOA: 'moa', AOA: 'aoa', Shareholding: 'shareholding',
          'Aadhar Propreitor': 'AadharCard', 'PAN Propreitor': 'panCard',
          ProvisionalBalanceSheet: 'provisionalBalanceSheet',
          AuditedBalanceFYPrev1: 'auditedBalanceFYPrev1',
          AuditedBalanceFYPrev2: 'auditedBalanceFYPrev2',
          'Aadhar Director': 'AadharCard', 'PAN Director': 'panCard',
          'Aadhar Partner': 'AadharCard', 'PAN Partner': 'panCard',
          BorrowerPanCard: 'borrowerPanCard',
          ApplicantAadhar: 'applicantAadhar',
          CoApplicantAadhar: 'coApplicantAadhar',
          ApplicantPan: 'applicantPan',
          CoApplicantPan: 'coApplicantPan',
          ApplicantForm16: 'applicantForm16',
          CoApplicantForm16: 'coApplicantForm16',
          ApplicantITR: 'applicantItr',
          CoApplicantITR: 'coApplicantItr',
          SalarySlips: 'salarySlips',
          JoiningLetter: 'joiningLetter',
          'GST-Docx': 'gstDocx',
          AS26: 'as26',
          'Other Relevant Docs': 'otherDocx',
        };
        if (normalizedBorrowerType === 'entity') typeToKeyMap['Other Relevant Docs'] = 'other';

        const MULTI_FILE_KEYS = new Set(['bankStatement', 'slLoan', 'panCard', 'AadharCard', 'salarySlips', 'applicantItr', 'coApplicantItr',]);
        const unknownDocTypes = new Set();

        const filesMap = documents.reduce((acc, doc) => {
          const key = typeToKeyMap[doc?.documentType];
          if (!key) { unknownDocTypes.add(doc?.documentType); return acc; }

          const normalized = {
            remote: true,
            url: doc?.documentPath || doc?.url || doc?.fileUrl || doc?.documentUrl || doc?.path || '',
            filename: doc?.documentName || doc?.originalName || doc?.fileName || doc?.name || `${doc?.documentType || 'file'}`,
            type: doc?.fileType || doc?.contentType || doc?.mimeType || '',
            size: doc?.size || 1,
            documentType: doc?.documentType,
          };

          if (!normalized.url) return acc;

          const pushDoc = (targetKey) => {
            if (!targetKey) return;
            if (MULTI_FILE_KEYS.has(targetKey)) {
              acc[targetKey] = acc[targetKey] ? [...(Array.isArray(acc[targetKey]) ? acc[targetKey] : [acc[targetKey]]), normalized] : [normalized];
            } else {
              acc[targetKey] = normalized;
            }
          };

          pushDoc(key);
          if (doc?.documentType === 'Aadhar Propreitor') pushDoc('applicantAadhar');
          if (doc?.documentType === 'PAN Propreitor') pushDoc('borrowerPanCard');
          return acc;
        }, {});

        if (unknownDocTypes.size) {
          toast.warn('Unmapped document types:', Array.from(unknownDocTypes));
        }

        const s = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v)));
        const prefill = {
          applicationId: apiData?.applicationId,
          applicationNumber: s(apiData?.applicationNumber),
          borrowerType: s(normalizedBorrowerType),
          entityType: s(normalizedEntityType),
          nameAsPerGST: s(apiData?.nameAsPerGST),
          mobileNo: s(apiData?.mobileNo || apiData?.userId?.mobileNumber),
          email: s(apiData?.email || apiData?.userId?.email),
          addr: s(apiData?.addr),
          profession: s(apiData?.profession),
          gstin: s(apiData?.gstin),
          udyamRegistrationNo: s(apiData?.udyamRegistrationNo),
          panNumber: s(apiData?.panNumber),
          totalSalesCurrentYear: apiData?.totalSalesCurrentYear ?? 0,
          lastFinancialYearTurnover: apiData?.lastFinancialYearTurnover ?? '',
          currentFinancialYearTurnover: apiData?.currentFinancialYearTurnover ?? apiData?.anticipatedProjectGrowthRateForCurrentYear ?? '',
          status: s(apiData?.status || 'incomplete'),
          fundRequirement: s(apiData?.fundRequirement ?? ''),
          endUseOfFund: s(apiData?.endUseOfFund),
          securityOffered: s(apiData?.securityOffered),
          descriptionOfSecurity: s(apiData?.descriptionOfSecurity),
          descriptionOfBusiness: s(apiData?.descriptionOfBusiness),
          companyCin: s(apiData?.companyCin),
          industry: s(apiData?.industry),
          sector: s(apiData?.sector),
          zipCode: s(apiData?.zipCode),
          state: s(apiData?.state),
          city: s(apiData?.city),
          anticipatedGrowthRateCurrentYear: apiData?.anticipatedProjectGrowthRateForCurrentYear ?? apiData?.currentFinancialYearTurnover ?? '',
          estimatedCollateralValue: apiData?.estimatedCollateralValue ?? '',
          userId: apiData?.userId?.id || apiData?.userId || undefined,
        };

        try {
          localStorage.removeItem('userBusinessType');
          localStorage.removeItem('userEntityType');
          localStorage.setItem('userBusinessType', prefill.borrowerType);
          localStorage.setItem('userEntityType', prefill.borrowerType === 'entity' ? (prefill.entityType || '') : 'individual');
        } catch (error)
        {
          toast.error('Error saving user business type and entity type:', error);
        }

        dispatch(setFormData(prefill));
        dispatch(setFiles(filesMap));
        dispatch(setActiveStep(0));
        dispatch(setResumed(true));
        navigate('/apply-loan', { state: { fromResumePage: true } });
      })
      .catch((error) => {
        setSnackbar({ open: true, message: error?.message || 'Failed to resume application', severity: 'error' });
      });
  }, [dispatch, navigate]);

  const enhancedTableColumns = useMemo(() => {
    const baseColumns = Array.isArray(loansColumns.columns) ? [...loansColumns.columns] : [];
    const isSavedProposals = activeTab === 1;
    
    const visibleColumns = isSavedProposals
      ? baseColumns.filter((c) => c.id !== 'reports' && c.id !== 'offers')
      : baseColumns;

    return visibleColumns.map((column) => {
      if (column.id === 'reports') {
        return {
          ...column,
          transformForFilter: (row) => [
            row?.proposalDetails?.onePager ? 'One Pager' : null,
            row?.proposalDetails?.cma ? 'CMA Report' : null,
          ].filter(Boolean),
          staticFilterOptions: ['One Pager', 'CMA Report'],
        };
      }
      if (column.id === 'offers') {
        return {
          ...column,
          transformForFilter: (row) =>
            Array.isArray(row?.offerForProposal)
              ? row.offerForProposal
                  .map((offer) => offer?.name || offer?.lenderName)
                  .filter((name) => name && String(name).trim() !== '')
              : [],
        };
      }      
      return column;
    });
  }, [activeTab]);

  const enhancedCellRenderers = useMemo(() => ({
    status: (row) => (
      <StatusChip
        label={row.status || "Submitted"}
        status={row.status || "Submitted"}
        size="small"
      />
    ),
    reports: (row) => {
      const borrowerType = String(row?.borrowerType || row?.BorrowerType || "").toLowerCase();
      const isIndividualBorrower = borrowerType === "individual";

      return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleOnePagerDownload(row)}
            // disabled={!row.proposalDetails?.onePager}
            disabled={!row.proposalDetails?.onePager || isIndividualBorrower}
            sx={{
              color: "#0052CC",
              borderColor: "#0052CC",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "rgba(0, 82, 204, 0.04)",
                borderColor: "#0052CC",
              },
              "&:disabled": {
                color: "#9E9E9E",
                borderColor: "#E0E0E0",
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            One Pager
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleCMAGeneration(row)}
            // disabled={!row.proposalDetails?.cma}
            disabled={!row.proposalDetails?.cma || isIndividualBorrower}
            sx={{
              color: "#0052CC",
              borderColor: "#0052CC",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "rgba(0, 82, 204, 0.04)",
                borderColor: "#0052CC",
              },
              "&:disabled": {
                color: "#9E9E9E",
                borderColor: "#E0E0E0",
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            CMA Report
          </Button>
        </Box>
      );
    },
    actions: (row) => (
      activeTab === 1 ? (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleResumeApplication(row)}
            sx={{
              backgroundColor: "#0052CC",
              color: "white",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#003E99" },
            }}
          >
            Continue Application
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {permission?.view === 1 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => handleViewApplication(row)}
              sx={{
                color: "#0052CC",
                borderColor: "#0052CC",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: "rgba(0, 82, 204, 0.04)",
                  borderColor: "#0052CC",
                },
              }}
            >
              View Application
            </Button>
          )}
          {row.proposal ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleViewProposal(row)}
                sx={{
                  color: "#0052CC",
                  borderColor: "#0052CC",
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 82, 204, 0.04)",
                    borderColor: "#0052CC",
                  },
                }}
              >
                View Proposal
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleUpdateProposal(row)}
                sx={{
                  color: "#0052CC",
                  borderColor: "#0052CC",
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 82, 204, 0.04)",
                    borderColor: "#0052CC",
                  },
                }}
              >
                Update Proposal
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleCreateProposal(row)}
              sx={{
                backgroundColor: "#4CAF50",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: "#43A047",
                },
              }}
            >
              Start Processing
            </Button>
          )}
        </Box>
      )
    ),
    offers: (row) => {
      const validOffers = Array.isArray(row?.offerForProposal)
        ? row.offerForProposal.filter((offer) => {
            const displayName = offer?.name || offer?.lenderName;
            const identifier = offer?.id || offer?.offerId || offer?.uuid;
            return Boolean(displayName) && Boolean(identifier);
          })
        : [];      
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {validOffers.length > 0 ? (
            validOffers.map((offer, index) => {
              const offerIdentifier = offer?.id ?? offer?.offerId ?? offer?.uuid;
              const displayName = offer?.name || offer?.lenderName;
              return (
                <Button
                  key={`${offerIdentifier || 'offer'}-${index}`}
                  variant="text"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!offerIdentifier) return;
                    navigate(`/offers/${offerIdentifier}`, { 
                      state: { offerFromLoan: offer } 
                    });
                  }}
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: "none",
                    color: "#0052CC",
                    '&:hover': { 
                      backgroundColor: "transparent", 
                      textDecoration: 'underline' 
                    }
                  }}
                >
                  {displayName}
                </Button>
              );
            })
          ) : (
            <Typography variant="body2" sx={{ color: "#263238" }}>
              No Offers are created yet
            </Typography>
          )}
        </Box>
      );
    },
  }), [permission, handleOnePagerDownload, handleCMAGeneration, handleViewApplication, handleViewProposal, handleUpdateProposal, handleCreateProposal, handleResumeApplication, navigate, activeTab]);

  const handleSearchInputChange = useCallback((event) => setSearchQuery(event.target.value), [setSearchQuery]);

  return (
    <PageWrapper>
      <PageHeader>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <PageTitle>Proposals</PageTitle>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="All Proposals" />
            <Tab label="Saved Proposals" />
          </Tabs>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Proposal ID, Application ID, Borrower Name, Mobile, Amount, or Status..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            sx={{ minWidth: 400 }}
            size="small"
          />
        </Box>
      </PageHeader>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={40} />
        </Box>
      ) : loanApplications.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No loan applications found.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyLoan}
            sx={{
              backgroundColor: "#0052CC",
              textTransform: "none",
            }}
          >
            Apply Now
          </Button>
        </Box>
      ) : (
        <CommonTable
          permissions={permission}
          columns={enhancedTableColumns}
          data={filteredLoanApplications}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
          noDataMessage="No loan applications found."
          customCellRenderer={enhancedCellRenderers}
          totalRecords={totalRecords}
        />
      )}
{console.log("Rendering Loans with data:", filteredLoanApplications)}
      <IncompleteLoanModal
        open={loanDetailsModalOpen}
        onClose={handleCloseLoanDetailsModal}
        mode="details"
        loan={selectedLoan}
      />

      <PreviewApplication
        open={previewOpen}
        onClose={handleClosePreview}
        data={previewData}
        readOnly={true}
      />

      <ApplyLoanNowModal
        open={showApplyLoanModal}
        onClose={() => setShowApplyLoanModal(false)}
      />

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default Loans;
